import { Dispatch, SetStateAction, createContext } from 'react';
import { ApprovalStages, Reviewer } from '@blisspointmedia/bpm-types/dist/CreativeApprovals';
import { AudioCompanionOption, IneligibilityReasonType } from '@Components/CreativeAllocation';

export const TabKey = {
  MENU: 'menu',
  WORKSPACE: 'workspace',
  CART: 'cart',
  APPROVALS: 'approvals',
  GENERATE: 'cloud'
} as const;
export type TabKeyType = (typeof TabKey)[keyof typeof TabKey];

export const NAVS: TabKeyType[] = [
  TabKey.MENU,
  TabKey.WORKSPACE,
  TabKey.CART,
  TabKey.APPROVALS,
  TabKey.GENERATE
];

export interface PlacementCreative {
  isci: string;
  pct: number;
  start_date: string;
  end_date?: string | null;
  creative_clickthrough_url?: string | null;
  audio_companion?: string | null;
  audio_companion_id?: string | null;
  has_active_or_future_flights?: boolean;
}

export const REMOVE_COMPANION = '__remove_companion__';

export interface CurrentCompanion {
  value?: string;
  mixed: boolean;
  distinctCount: number;
  bannerCount: number;
}

export interface Placement {
  id: string;
  name: string;
  network: string;
  network_label?: string;
  network_group?: string;
  platform: string;
  length: string | null;
  campaign?: string | null;
  adServer: string;
  rotationKey: string;
  dimensions?: string;
  flashtalkingPlacementType?: string;
  vendor?: string;
  creatives: PlacementCreative[];
  placement_clickthrough_url?: string | null;
  placement_start?: string;
  placement_end?: string;
  tracking_pixels?: string[];
}

export interface ShopData {
  placements: Placement[];
  placementMap: Record<string, Placement>;
}

export interface WorkspaceOption {
  isci: string;
  name: string; // the ISCI, used as the rotation key
  creative: string; // the creative's display name
  color: string;
  length?: number;
  // Eligibility for the current placement context (null/undefined = eligible).
  reason?: IneligibilityReasonType | null;
  disabledReason?: string;
}

export interface RotationItem {
  isci: string;
  value: number;
  color?: string;
  audioCompanion?: string;
  clickthroughURL?: string;
}

export interface CartItem {
  id: number;
  placements: string[];
  rotation: RotationItem[];
}

export interface WorkspaceMetadata {
  audioCompanion?: string;
  clickthroughURL?: string;
}

export interface ApprovalStageData {
  stage?: ApprovalStages;
  reviewers?: Reviewer[];
  requester?: string;
  currentReviewer?: string;
}

export interface StreamingCreativesContextValue {
  tab: TabKeyType;
  goToTab: (tab: TabKeyType) => void;
  date: string;
  shopData: ShopData;
  cart: CartItem[];
  selectedMap: Partial<Record<string, boolean>>;
  inCart: Record<string, boolean>;
  setSelectedValue: (key: string, value: boolean) => void;
  setSelectedMap: Dispatch<SetStateAction<Partial<Record<string, boolean>>>>;
  workspaceOptions: WorkspaceOption[];
  workspaceMap: Partial<Record<string, number>>;
  setWorkspaceValue: (key: string, value: number) => void;
  setWorkspaceMap: Dispatch<SetStateAction<Partial<Record<string, number>>>>;
  workspaceMetadataMap: Partial<Record<string, WorkspaceMetadata>>;
  setWorkspaceMetadata: (key: string, value: WorkspaceMetadata) => void;
  setWorkspaceMetadataMap: Dispatch<SetStateAction<Partial<Record<string, WorkspaceMetadata>>>>;
  addToCart: (item: CartItem) => void;
  committing: boolean;
  commit: () => Promise<void>;
  setCart: Dispatch<SetStateAction<CartItem[]>>;
  audioCompanionOptions: AudioCompanionOption[];
  approvalStageData?: ApprovalStageData;
  updateApprovalStage: (
    company: string,
    newApprovalStage: ApprovalStages | string,
    selectedReviewers: Reviewer[],
    currentRequester: string,
    currentReviewer?: string | null,
    commentBoxText?: string
  ) => void;
  platform: string;
  setPlatform: Dispatch<SetStateAction<string>>;
  adServer: string;
  setAdServer: Dispatch<SetStateAction<string>>;
  dimensions: string;
  setDimensions: Dispatch<SetStateAction<string>>;
  length: string | null;
  setLength: Dispatch<SetStateAction<string | null>>;
  flashtalkingPlacementType: string;
  setFlashtalkingPlacementType: Dispatch<SetStateAction<string>>;
}

export const StreamingCreativesContext = createContext<StreamingCreativesContextValue>(
  {} as StreamingCreativesContextValue
);
